/** @typedef {'en'|'fr'} AppLocale */

/**
 * @typedef {Object} LibraryLinkItem
 * @property {string} id
 * @property {AppLocale} lang
 * @property {string} title
 * @property {string} summary
 * @property {string} url
 * @property {string} [sceneId]
 * @property {number} [readMin]
 */

/**
 * @typedef {Object} LibraryArticle
 * @property {string} id
 * @property {AppLocale} lang
 * @property {string} title
 * @property {string} summary
 * @property {string} body
 * @property {string} [url]
 * @property {{ sceneId: string, tagline: string, readMin: number, rating: number, mood: string[], overlay?: string }} scene
 */

/** @type {Record<string, object>} */
export const BOOK_SCENE_BY_ID = {
  book_atomic_habits: {
    sceneId: "atomic_stairs",
    overlay: "Small actions become extraordinary lives.",
    tagline: "Small habits create remarkable results.",
    readMin: 8,
    rating: 4.9,
    mood: ["Hope", "Growth", "Progress"],
  },
  book_dopamine_nation: {
    sceneId: "dopamine_split",
    tagline: "Balance pleasure and pain in a distracted world.",
    readMin: 9,
    rating: 4.8,
    mood: ["Clarity", "Balance", "Awareness"],
  },
};

export const DEFAULT_BOOK_SCENE = {
  sceneId: "journey_path",
  tagline: "A new chapter in your transformation.",
  readMin: 8,
  rating: 4.8,
  mood: ["Growth"],
};

/** In-app articles (English). */
export const LIBRARY_ARTICLES_EN = [
  {
    id: "en-break-bad-habit",
    lang: "en",
    title: "How to Break a Bad Habit",
    summary: "James Clear on replacing bad habits, cutting triggers, and building awareness.",
    body:
      "Bad habits are often responses to stress and boredom. You rarely eliminate them by force alone \u2014 you replace them with a healthier behavior that meets the same need.\n\nStart with awareness: when does it happen, where, with whom, and what triggers it? Then choose a substitute in advance, remove triggers from your environment, and plan for slip-ups so you can return quickly.\n\nRead the full guide from James Clear for deeper practice.",
    url: "https://jamesclear.com/how-to-break-a-bad-habit",
    scene: {
      sceneId: "habit_tree",
      tagline: "Replace the habit. Keep the need met.",
      readMin: 6,
      rating: 4.9,
      mood: ["Habits", "Clarity", "Action"],
    },
  },
  {
    id: "en-stop-adult-content",
    lang: "en",
    title: "7 Ways to Stop Watching Adult Content",
    summary: "Practical steps from Verywell Mind for reducing compulsive adult content use.",
    body:
      "Recovery often starts with honest awareness, replacing the habit with healthier coping, and redesigning your environment so triggers are harder to reach.\n\nSupport, accountability, and patience matter. Progress is usually gradual \u2014 each urge you ride out without acting weakens the old loop.\n\nOpen the full Verywell Mind article for the complete list of strategies.",
    url: "https://www.verywellmind.com/7-ways-to-stop-watching-porn-5204044",
    scene: {
      sceneId: "urge_brain",
      tagline: "Practical steps toward freedom.",
      readMin: 5,
      rating: 4.8,
      mood: ["Recovery", "Support", "Hope"],
    },
  },
  {
    id: "en-adult-content-recovery",
    lang: "en",
    title: "Adult Content Addiction Recovery",
    summary: "What recovery can look like: habits, withdrawal, and relapse prevention.",
    body:
      "Recovery from adult content addiction involves reducing compulsive behavior, strengthening self-control, and building a healthier relationship with stress, technology, and sexuality.\n\nMany people work through withdrawal, safer tech routines, accountability, and relapse-prevention plans. Progress happens step by step.\n\nVisit recovery resources for a fuller overview of timelines and FAQs.",
    url: "https://www.pornaddiction.org/recovery/",
    scene: {
      sceneId: "identity_door",
      tagline: "Healing one choice at a time.",
      readMin: 5,
      rating: 4.8,
      mood: ["Recovery", "Healing", "Growth"],
    },
  },
];

/** In-app articles (French). */
export const LIBRARY_ARTICLES_FR = [
  {
    id: "fr-briser-mauvaise-habitude",
    lang: "fr",
    title: "Comment briser une mauvaise habitude",
    summary: "Remplacer l'habitude, r\u00e9duire les d\u00e9clencheurs, et cultiver la conscience \u2014 d'apr\u00e8s James Clear.",
    body:
      "Les mauvaises habitudes r\u00e9pondent souvent au stress et \u00e0 l'ennui. On ne les \u00e9limine pas seulement par la force : on les remplace par un comportement plus sain qui r\u00e9pond au m\u00eame besoin.\n\nCommencez par la conscience : quand cela arrive, o\u00f9, avec qui, et quels d\u00e9clencheurs. Puis choisissez un substitut \u00e0 l'avance, retirez les d\u00e9clencheurs de votre environnement, et pr\u00e9voyez les \u00e9carts pour revenir rapidement.\n\nLisez le guide complet (en anglais) de James Clear pour aller plus loin.",
    url: "https://jamesclear.com/how-to-break-a-bad-habit",
    scene: {
      sceneId: "habit_tree",
      tagline: "Remplacez l'habitude. Gardez le besoin satisfait.",
      readMin: 6,
      rating: 4.9,
      mood: ["Habitudes", "Clart\u00e9", "Action"],
    },
  },
  {
    id: "fr-arreter-contenu-adulte",
    lang: "fr",
    title: "7 fa\u00e7ons d'arr\u00eater le contenu adulte",
    summary: "Des \u00e9tapes concr\u00e8tes vers la libert\u00e9.",
    body:
      "Le chemin vers la libert\u00e9 commence souvent par une d\u00e9cision courageuse : prendre conscience de la situation, changer ses habitudes et cr\u00e9er un environnement qui facilite le passage \u00e0 l'action.\n\nLe soutien, la responsabilit\u00e9 et la pers\u00e9v\u00e9rance jouent un r\u00f4le essentiel. Le progr\u00e8s se fait pas \u00e0 pas, et chaque petit pas compte.\n\nConsultez l'article complet sur Verywell Mind pour d\u00e9couvrir l'ensemble des strat\u00e9gies.",
    url: "https://www.verywellmind.com/7-ways-to-stop-watching-porn-5204044",
    scene: {
      sceneId: "urge_brain",
      tagline: "Des \u00e9tapes concr\u00e8tes vers la libert\u00e9.",
      readMin: 5,
      rating: 4.8,
      mood: ["R\u00e9tablissement", "Soutien", "Espoir"],
    },
  },
  {
    id: "fr-guerison-contenu-adulte",
    lang: "fr",
    title: "R\u00e9tablissement de la d\u00e9pendance au contenu adulte",
    summary: "\u00c0 quoi peut ressembler le r\u00e9tablissement : habitudes, sevrage et pr\u00e9vention des rechutes.",
    body:
      "Le r\u00e9tablissement implique de r\u00e9duire le comportement compulsif, de renforcer la ma\u00eetrise de soi, et de b\u00e2tir une relation plus saine avec le stress, la technologie et la sexualit\u00e9.\n\nBeaucoup passent par le sevrage, des routines tech plus s\u00fbres, la responsabilisation et des plans anti-rechute. Le progr\u00e8s se fait \u00e9tape par \u00e9tape.\n\nVisitez PornAddiction.org Recovery pour un aper\u00e7u plus complet.",
    url: "https://www.pornaddiction.org/recovery/",
    scene: {
      sceneId: "identity_door",
      tagline: "Gu\u00e9rir, un choix \u00e0 la fois.",
      readMin: 5,
      rating: 4.8,
      mood: ["Gu\u00e9rison", "Croissance", "Espoir"],
    },
  },
];

/** @type {LibraryArticle[]} */
export const LIBRARY_ARTICLES = [...LIBRARY_ARTICLES_EN, ...LIBRARY_ARTICLES_FR];

/** English-language YouTube videos. */
const PODCASTS_EN = [
  {
    id: "pod-en-ob_ryvnU9sY",
    lang: "en",
    videoId: "Ob_ryvnU9sY",
    channel: "YouTube",
    title: "Recovery talk",
    summary: "Watch inside Procsystim \u2014 recovery-focused conversation.",
    url: "https://www.youtube.com/watch?v=Ob_ryvnU9sY",
    sceneId: "urge_surf",
    readMin: 15,
  },
  {
    id: "pod-en-kYxMt2arhlg",
    lang: "en",
    videoId: "kYxMt2arhlg",
    channel: "YouTube",
    title: "Mindset & discipline",
    summary: "In-app video on discipline and staying consistent.",
    url: "https://www.youtube.com/watch?v=kYxMt2arhlg",
    sceneId: "focus_cube",
    readMin: 12,
  },
  {
    id: "pod-en-lS3ddSQLLYs",
    lang: "en",
    videoId: "lS3ddSQLLYs",
    channel: "YouTube",
    title: "Breaking free",
    summary: "Tools for breaking free from compulsive loops.",
    url: "https://www.youtube.com/watch?v=lS3ddSQLLYs",
    sceneId: "journey_path",
    readMin: 14,
  },
  {
    id: "pod-en-t6RCTP4fc9Q",
    lang: "en",
    videoId: "t6RCTP4fc9Q",
    channel: "YouTube",
    title: "Awareness & recovery",
    summary: "Awareness and recovery resource.",
    url: "https://www.youtube.com/watch?v=t6RCTP4fc9Q",
    sceneId: "env_split",
    readMin: 12,
  },
];


/** French-language YouTube videos. */
const PODCASTS_FR = [
  {
    id: "pod-fr-NxcvqbQ7oGI",
    lang: "fr",
    videoId: "NxcvqbQ7oGI",
    channel: "YouTube",
    title: "Les habitudes qui durent",
    summary: "Construire des habitudes durables au quotidien.",
    url: "https://www.youtube.com/watch?v=NxcvqbQ7oGI",
    sceneId: "atomic_stairs",
    readMin: 13,
  },
  {
    id: "pod-fr-MTc7x45KEg4",
    lang: "fr",
    videoId: "MTc7x45KEg4",
    channel: "YouTube",
    title: "Parcours de guérison",
    summary: "Discussion sur le parcours de rétablissement.",
    url: "https://www.youtube.com/watch?v=MTc7x45KEg4",
    sceneId: "identity_door",
    readMin: 15,
  },
];

/** @type {LibraryLinkItem[]} */
export const LIBRARY_PODCASTS = [...PODCASTS_EN, ...PODCASTS_FR];

/** @param {string} bookId */
export function getBookSceneMeta(bookId) {
  return BOOK_SCENE_BY_ID[bookId] ?? DEFAULT_BOOK_SCENE;
}

/**
 * @param {AppLocale} lang
 * @returns {LibraryArticle[]}
 */
export function getArticlesForLang(lang) {
  return LIBRARY_ARTICLES.filter((a) => a.lang === lang);
}

/**
 * @param {AppLocale} lang
 */
export function getPodcastsForLang(lang) {
  return LIBRARY_PODCASTS.filter((p) => p.lang === lang);
}

/**
 * @param {LibraryLinkItem} podcast
 * @param {AppLocale} _lang
 */
export function podcastDisplayTitle(podcast, _lang) {
  return podcast.title;
}

/**
 * @param {LibraryLinkItem} podcast
 * @param {AppLocale} _lang
 */
export function podcastDisplaySummary(podcast, _lang) {
  return podcast.summary;
}
